import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['form', 'radio', 'checkbox', 'textarea', 'number']
  static values = {
    sessionToken: String,
    currentQuestion: Number,
    totalQuestions: Number,
    testId: Number
  }

  connect() {
    // Автосохранение при изменении ответа
    this.setupAutoSave()
  }

  disconnect() {
    // Очистка при отключении
  }

  setupAutoSave() {
    // Автосохранение и переход для радиокнопок (single_choice)
    if (this.hasRadioTarget) {
      this.radioTargets.forEach(radio => {
        radio.addEventListener('change', async () => {
          await this.saveAndGoToNext()
        })
      })
    }

    // Автосохранение для чекбоксов
    if (this.hasCheckboxTarget) {
      this.checkboxTargets.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.saveAnswer()
        })
      })
    }

    // Автосохранение для текстовых полей
    if (this.hasTextareaTarget) {
      this.textareaTargets.forEach(textarea => {
        textarea.addEventListener('blur', () => {
          this.saveAnswer()
        })
      })
    }

    // Автосохранение для числовых полей
    if (this.hasNumberTarget) {
      this.numberTargets.forEach(number => {
        number.addEventListener('blur', () => {
          this.saveAnswer()
        })
      })
    }
  }

  async saveAnswer() {
    if (!this.hasFormTarget) return false

    const formData = new FormData(this.formTarget)
    const questionOrder = formData.get('question_order')

    // Собираем данные ответа в зависимости от типа вопроса
    const answerData = this.collectAnswerData()

    if (Object.keys(answerData).length === 0) {
      // Нет данных для сохранения
      return false
    }

    try {
      const response = await fetch(this.formTarget.action, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          question_order: questionOrder,
          answer: answerData
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          // Ответ успешно сохранен
          console.log('Ответ сохранен')
          return data
        }
      }
      return { success: false }
    } catch (error) {
      console.error('Ошибка при сохранении ответа:', error)
      return false
    }
  }

  collectAnswerData() {
    // Для single_choice
    if (this.hasRadioTarget) {
      const checked = this.radioTargets.find(radio => radio.checked)
      if (checked) {
        return { answer_id: checked.value }
      }
    }

    // Для multiple_choice
    if (this.hasCheckboxTarget) {
      const checked = this.checkboxTargets.filter(checkbox => checkbox.checked)
      if (checked.length > 0) {
        return { answer_ids: checked.map(checkbox => checkbox.value) }
      }
    }

    // Для text
    if (this.hasTextareaTarget) {
      const textarea = this.textareaTargets[0]
      if (textarea && textarea.value) {
        return { text: textarea.value }
      }
    }

    // Для number/scale
    if (this.hasNumberTarget) {
      const number = this.numberTargets[0]
      if (number && number.value) {
        return { value: parseFloat(number.value) }
      }
    }

    return {}
  }

  async saveAndGoToNext() {
    // Сохраняем ответ и получаем информацию о следующем вопросе
    const result = await this.saveAnswer()
    if (!result || result === false || !result.success) {
      console.error('Не удалось сохранить ответ')
      return
    }

    // Если это последний вопрос, автоматически завершаем тест
    if (result.is_last_question) {
      // Небольшая задержка для лучшего UX
      setTimeout(() => {
        this.completeTest()
      }, 300)
      return
    }

    // Переход к следующему вопросу
    if (result.next_question_index !== null && result.next_question_index !== undefined) {
      // Небольшая задержка для лучшего UX
      setTimeout(() => {
        const url = new URL(window.location.href)
        url.searchParams.set('question', result.next_question_index)
        window.location.href = url.toString()
      }, 300)
    }
  }

  async nextQuestion() {
    // Сохраняем ответ перед переходом (если еще не сохранен)
    const result = await this.saveAnswer()

    if (!result || !result.success) return

    // Получаем текущий индекс из URL или вычисляем
    const url = new URL(window.location.href)
    const currentIndex = url.searchParams.get('question') ? parseInt(url.searchParams.get('question')) : 0
    const nextIndex = currentIndex + 1

    // Проверяем, есть ли следующий вопрос
    const totalQuestions = result.total_questions || this.getTotalQuestions()
    if (nextIndex >= totalQuestions) {
      // Это последний вопрос, автоматически завершаем тест
      this.completeTest()
      return
    }

    // Обновляем URL для перехода к следующему вопросу (используем индекс)
    url.searchParams.set('question', nextIndex)
    window.location.href = url.toString()
  }

  async completeTest() {
    // Получаем test_id и session_token из data-атрибутов
    const testId = this.testIdValue
    // Получаем session_token из URL (последняя часть пути)
    const sessionToken = new URL(window.location.href).pathname.split('/').pop()

    if (!testId || !sessionToken) {
      console.error('Не удалось получить test_id или session_token', { testId, sessionToken })
      return
    }

    // Создаем скрытую форму для POST запроса
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/tests/${testId}/complete`

    // Добавляем CSRF токен
    const csrfToken = document.querySelector('meta[name="csrf-token"]')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken.content
      form.appendChild(csrfInput)
    }

    // Добавляем session_token
    const sessionInput = document.createElement('input')
    sessionInput.type = 'hidden'
    sessionInput.name = 'session_token'
    sessionInput.value = sessionToken
    form.appendChild(sessionInput)

    // Добавляем форму в DOM и отправляем
    document.body.appendChild(form)
    form.submit()
  }

  getTotalQuestions() {
    // Получаем общее количество вопросов из DOM
    const progressText = document.querySelector('.text-sm.font-medium.text-gray-700')
    if (progressText) {
      const match = progressText.textContent.match(/из (\d+)/)
      if (match) {
        return parseInt(match[1])
      }
    }
    // Fallback - пытаемся получить из data-атрибута или возвращаем большое число
    return 999
  }

  async previousQuestion() {
    await this.saveAnswer()

    // Получаем текущий индекс из URL
    const url = new URL(window.location.href)
    const currentIndex = url.searchParams.get('question') ? parseInt(url.searchParams.get('question')) : 0
    const prevIndex = Math.max(0, currentIndex - 1)

    // Обновляем URL для перехода к предыдущему вопросу (используем индекс)
    url.searchParams.set('question', prevIndex)
    window.location.href = url.toString()
  }
}

